import {
    Alert,
    Box,
    Button,
    Divider,
    Grid,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import { HabitatAdminUser, OrgOverview } from '@types'
import { Fragment, useEffect, useState } from 'react'
import { useRoute } from 'wouter'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import ReviewModal from '../ReviewModal'
import { navigate } from 'wouter/use-location'
import API from '@enums/api'

export default function ReviewOrgPage(): JSX.Element {
    const [, params] = useRoute('/review-orgs/:orgId')
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | undefined>()

    const [org, setOrg] = useState<OrgOverview | undefined>()
    const [admins, setAdmins] = useState<
        Pick<
            HabitatAdminUser,
            'id' | 'email' | 'first_name' | 'last_name' | 'role'
        >[]
    >([])
    const [decision, setDecision] = useState<
        'approved' | 'denied' | undefined
    >()

    useEffect(function init() {
        loadOrg()
    }, [])

    async function loadOrg() {
        if (!params) {
            setError('No organization ID was provided!')
            return
        }
        setLoading(true)

        const { orgId } = params

        const orgRes = await API.ha.orgs.getOrg(orgId)

        if (!orgRes.ok) {
            setError(`Failed to find org '${orgId}'`)
            setLoading(false)
            return
        }

        setOrg(orgRes.value)

        const adminsRes = await API.searches.listOrgAdmins(orgId)

        if (!adminsRes.ok) {
            console.error(adminsRes.error)
            setError(`Failed to retrieve admins for org '${orgId}'`)
            setLoading(false)
            return
        }

        setAdmins(adminsRes.value)

        setLoading(false)
    }

    if (loading) {
        return <DashboardLayout loading />
    }

    if (error) {
        return (
            <DashboardLayout>
                <Alert severity="error">{error}</Alert>
            </DashboardLayout>
        )
    }

    if (!org) {
        return (
            <DashboardLayout>
                <Alert severity="error">No org found.</Alert>
            </DashboardLayout>
        )
    }

    if (org.review_date) {
        return (
            <DashboardLayout>
                <Alert severity="info">
                    {org.name} has already been reviewed.
                </Alert>
            </DashboardLayout>
        )
    }

    return (
        <DashboardLayout>
            <Box mx={'1em'}>
                <Button href="/review-orgs" variant="text">
                    <KeyboardBackspaceIcon fontSize="small" /> Approval Requests
                </Button>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h1">{org.name}</Typography>
                    <Stack direction="row" gap={1} maxHeight={'50%'}>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => setDecision('approved')}
                        >
                            Approve
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setDecision('denied')}
                        >
                            Deny
                        </Button>
                    </Stack>
                </Stack>
                <Stack direction={'column'} gap={2}>
                    <Paper>
                        <Typography variant="h2">
                            Organization Details
                        </Typography>
                        <Divider role="presentation" sx={{ my: '1em' }} />
                        <Grid container gap={2}>
                            <DetailGridItem
                                title="Name of Organization"
                                data={org.name}
                            />
                            <DetailGridItem
                                title="Address"
                                data={[
                                    org.address ?? '',
                                    org.city ?? '',
                                    org.state ?? '',
                                    org.country ?? '',
                                    org.postcode ?? '',
                                ].join(', ')}
                            />
                            <DetailGridItem
                                title="Number of Users Expected"
                                data={org.expected_users}
                            />
                            <DetailGridItem
                                title="Timezone"
                                data={org.timezone ?? 'N/A'}
                            />
                            <DetailGridItem
                                title="Currency"
                                data={org.currency ?? 'N/A'}
                            />
                        </Grid>
                    </Paper>
                    <Paper>
                        <Typography variant="h2">Admin Details</Typography>
                        {admins.map(({ first_name, last_name, email }, i) => (
                            <Fragment key={email}>
                                {i > 0 && (
                                    <Divider
                                        role="presentation"
                                        sx={{ my: '1em' }}
                                    />
                                )}
                                <Grid container gap={2}>
                                    <DetailGridItem
                                        title="First Name"
                                        data={first_name ?? 'N/A'}
                                    />
                                    <DetailGridItem
                                        title="Last Name"
                                        data={last_name ?? 'N/A'}
                                    />
                                    <DetailGridItem
                                        title="Email"
                                        data={email ?? 'N/A'}
                                    />
                                </Grid>
                            </Fragment>
                        ))}
                    </Paper>
                </Stack>
            </Box>
            <ReviewModal
                org={org}
                status={decision}
                onClose={() => setDecision(undefined)}
                onReviewed={() => navigate('/review-orgs')}
            />
        </DashboardLayout>
    )
}

interface DetailItemProps {
    title: string
    data: string | number
}
function DetailGridItem({ title, data }: DetailItemProps): JSX.Element {
    return (
        <Grid item xs={12} md={6} lg={3}>
            <Stack direction={'column'}>
                <Typography variant="subtitle1" component="p">
                    {title}
                </Typography>
                <Typography variant="body1">{data}</Typography>
            </Stack>
        </Grid>
    )
}
